/**
 * One campaign, live.
 *
 * The funnel is stated in full - targeted → sent → delivered → opened →
 * converted - with the suppressed and failed counts beside it rather than hidden.
 * A campaign that reached 4,000 of 12,000 people is a fact the operator needs on
 * this screen, not a number they have to go and derive.
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Copy, Pause, Play, RefreshCw } from 'lucide-react'
import { useColors } from '../../hooks/useColors'
import {
  Card, CardTitle, Chip, PageHeader, PillButton, SelectInput,
  TableHead, Td,
} from '../../components/shared/primitives'
import '../../styles/notifications.css'
import NotificationPreview from '../../features/notifications/components/NotificationPreview'
import { notificationsApi } from '../../features/notifications/api'
import { PROVIDER_ERROR, STATUS_LABEL, STATUS_TONE, SUPPRESSION_LABEL, SUPPRESSION_RECOVERY } from '../../features/notifications/constants'
import type { Campaign, Delivery, Funnel } from '../../features/notifications/types'

type Analytics = Funnel & {
  suppression: { reason: string; count: number }[]
  events: Record<string, number>
  failures: { error: string; count: number }[]
}

const money = (value: number) => `₹${Math.round(value).toLocaleString('en-IN')}`

function Stat({ label, value, hint, accent }: {
  label: string
  value: string
  hint?: string
  accent?: boolean
}) {
  return (
    <div className="ntf-stat">
      <div className="ntf-stat__label">{label}</div>
      <div
        className="ntf-stat__value font-display"
        style={accent ? { color: 'var(--accent-text)' } : undefined}
      >
        {value}
      </div>
      {hint ? <div className="ntf-stat__hint">{hint}</div> : null}
    </div>
  )
}

export default function CampaignDetailPage() {
  const colors = useColors()
  const navigate = useNavigate()
  const { id } = useParams()

  const [campaign, setCampaign] = useState<Campaign | null>(null)
  const [analytics, setAnalytics] = useState<Analytics | null>(null)
  const [deliveries, setDeliveries] = useState<Delivery[]>([])
  const [deliveryFilter, setDeliveryFilter] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    if (!id) return
    try {
      const [detail, deliveryPage] = await Promise.all([
        notificationsApi.getCampaign(id),
        notificationsApi.listDeliveries(id, { status: deliveryFilter || undefined, limit: 100 }),
      ])
      setCampaign(detail.campaign)
      setAnalytics(detail.analytics)
      setDeliveries(deliveryPage.deliveries)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load this campaign')
    }
  }, [id, deliveryFilter])

  useEffect(() => {
    void load()
  }, [load])

  // While a campaign is mid-flight the numbers move every couple of seconds.
  // Polling only the in-flight states, and only this campaign, is cheap and is
  // the difference between "is this working?" and watching it work. Nothing
  // here reloads the page.
  useEffect(() => {
    if (!campaign || !['scheduled', 'sending'].includes(campaign.status)) return undefined
    const timer = setInterval(() => void load(), 2000)
    return () => clearInterval(timer)
  }, [campaign, load])

  const act = async (action: () => Promise<unknown>) => {
    setBusy(true)
    setError('')
    try {
      await action()
      await load()
    } catch (err) {
      const response = (err as { response?: { data?: { message?: string } } }).response
      setError(response?.data?.message || 'Action failed')
    } finally {
      setBusy(false)
    }
  }

  if (!campaign) {
    return (
      <div>
        <PageHeader title="Campaign" />
        <Card>{error || 'Loading…'}</Card>
      </div>
    )
  }

  const live = ['scheduled', 'sending'].includes(campaign.status)
  // Everything the worker has reached a verdict on, not just successes.
  const processed = (analytics?.sent ?? 0) + (analytics?.suppressed ?? 0) + (analytics?.failed ?? 0)
  const pctDone = analytics?.targeted ? Math.min(100, Math.round((processed / analytics.targeted) * 100)) : 0
  // The dominant failure, if it is one the operator has to fix rather than wait out.
  const blocking = (analytics?.failures ?? []).find((f) => PROVIDER_ERROR[f.error]?.blocking) ?? null
  const pausable = ['scheduled', 'sending'].includes(campaign.status)

  return (
    <div>
      <PageHeader
        title={campaign.name}
        subtitle={`${campaign.category} · created ${new Date(campaign.created_at).toLocaleDateString('en-IN')}`}
        actions={(
          <>
            <Chip tone={STATUS_TONE[campaign.status]}>{STATUS_LABEL[campaign.status]}</Chip>
            <PillButton variant="ghost" onClick={() => void load()}>
              <RefreshCw size={14} /> Refresh
            </PillButton>
            <PillButton
              variant="secondary"
              onClick={() => act(async () => {
                const copy = await notificationsApi.duplicateCampaign(campaign.id)
                navigate(`/notifications/${copy.id}/edit`)
              })}
              disabled={busy}
            >
              <Copy size={14} /> Duplicate
            </PillButton>
            {pausable ? (
              <PillButton variant="outline" onClick={() => act(() => notificationsApi.pauseCampaign(campaign.id))} disabled={busy}>
                <Pause size={14} /> Pause
              </PillButton>
            ) : null}
            {campaign.status === 'paused' ? (
              <PillButton onClick={() => act(() => notificationsApi.resumeCampaign(campaign.id))} disabled={busy}>
                <Play size={14} /> Resume
              </PillButton>
            ) : null}
            <PillButton variant="ghost" onClick={() => navigate('/notifications')}>
              <ArrowLeft size={14} /> Back
            </PillButton>
          </>
        )}
      />

      {error ? (
        <Card style={{ marginBottom: '16px', borderColor: '#B45309' }}>
          <span style={{ color: '#B45309' }}>{error}</span>
        </Card>
      ) : null}

      {blocking ? (
        <Card style={{ marginBottom: '16px', borderColor: '#B45309', background: 'rgba(180,83,9,0.04)' }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <AlertTriangle size={17} color="#B45309" style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontWeight: 600, color: '#B45309' }}>
                Delivery is blocked: {PROVIDER_ERROR[blocking.error]?.label ?? blocking.error}
              </div>
              <div style={{ fontSize: '12.5px', color: colors.textSecondary, marginTop: '4px', lineHeight: 1.55 }}>
                {blocking.count.toLocaleString('en-IN')} of {(analytics?.targeted ?? 0).toLocaleString('en-IN')}{' '}
                deliveries failed with <code>{blocking.error}</code>. This is not a per-customer
                problem and retrying will not help.
              </div>
              {PROVIDER_ERROR[blocking.error]?.fix ? (
                <div style={{ fontSize: '12.5px', color: colors.textPrimary, marginTop: '6px', lineHeight: 1.55 }}>
                  {PROVIDER_ERROR[blocking.error]?.fix}
                </div>
              ) : null}
            </div>
          </div>
        </Card>
      ) : null}

      {live ? (
        <Card style={{ marginBottom: '16px', borderColor: '#D6B97B' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ color: colors.textPrimary, fontWeight: 600 }}>
              {campaign.status === 'scheduled' ? 'Starting' : 'Sending now'}
            </span>
            <span style={{ color: colors.textMuted, fontSize: '12.5px', fontVariantNumeric: 'tabular-nums' }}>
              {processed.toLocaleString('en-IN')} of {(analytics?.targeted ?? 0).toLocaleString('en-IN')} processed
              {' · '}{(analytics?.sent ?? 0).toLocaleString('en-IN')} sent
            </span>
          </div>
          <div
            style={{ marginTop: '10px', height: '4px', borderRadius: '999px', background: colors.surface, overflow: 'hidden' }}
            role="progressbar"
            aria-valuenow={pctDone}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div style={{ width: `${pctDone}%`, height: '100%', background: '#D6B97B', transition: 'width .4s ease' }} />
          </div>
          <div style={{ marginTop: '8px', fontSize: '12px', color: colors.textMuted }}>
            Updating live. You can leave this page; sending continues in the background.
          </div>
        </Card>
      ) : null}

      <div className="ntf-stats">
        <Stat label="Targeted" value={(analytics?.targeted ?? 0).toLocaleString('en-IN')} />
        <Stat
          label="Sent"
          value={(analytics?.sent ?? 0).toLocaleString('en-IN')}
          hint={`${(analytics?.suppressed ?? 0).toLocaleString('en-IN')} skipped`}
        />
        <Stat label="Opened" value={(analytics?.opened ?? 0).toLocaleString('en-IN')} />
        <Stat
          label="Open rate"
          value={`${(analytics?.open_rate ?? 0).toFixed(1)}%`}
          hint={analytics?.denominator === 'sent' ? 'over sent (no receipts)' : 'over delivered'}
        />
        <Stat label="Orders" value={(analytics?.converted ?? 0).toLocaleString('en-IN')} />
        <Stat
          label="Revenue"
          value={money(analytics?.revenue ?? 0)}
          hint={`${money(analytics?.revenue_per_notification ?? 0)} per push`}
          accent
        />
      </div>

      <div className="ntf-split ntf-split--wide" style={{ marginTop: '18px' }}>
        <Card padded={false}>
          <div style={{ padding: '18px 20px 0' }}>
            <CardTitle
              right={(
                <SelectInput
                  value={deliveryFilter}
                  onChange={(event) => setDeliveryFilter(event.target.value)}
                  style={{ width: '170px', padding: '8px 10px' }}
                  aria-label="Filter deliveries"
                >
                  <option value="">All recipients</option>
                  <option value="sent,delivered,opened,converted">Reached</option>
                  <option value="opened,converted">Opened</option>
                  <option value="suppressed">Skipped</option>
                  <option value="failed">Failed</option>
                  <option value="queued">Still queued</option>
                </SelectInput>
              )}
            >
              Recipients
            </CardTitle>
          </div>
          <div style={{ overflowX: 'auto', maxHeight: '520px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <TableHead
                columns={[
                  { key: 'user', label: 'Customer' },
                  { key: 'status', label: 'Outcome' },
                  { key: 'sent', label: 'Sent', align: 'right' },
                  { key: 'opened', label: 'Opened', align: 'right' },
                  { key: 'revenue', label: 'Revenue', align: 'right' },
                ]}
              />
              <tbody>
                {deliveries.map((delivery) => (
                  <tr key={delivery.id}>
                    <Td>{delivery.user_name || 'Customer'}</Td>
                    <Td>
                      {delivery.status === 'suppressed' ? (
                        <Chip tone="neutral">
                          {SUPPRESSION_LABEL[delivery.suppressed_reason ?? ''] ?? delivery.suppressed_reason}
                        </Chip>
                      ) : delivery.status === 'failed' ? (
                        <Chip tone="warning" style={{ maxWidth: '220px' }}>{delivery.error || 'Failed'}</Chip>
                      ) : (
                        <Chip tone={delivery.status === 'converted' ? 'success' : delivery.status === 'opened' ? 'info' : 'neutral'}>
                          {delivery.status}
                        </Chip>
                      )}
                    </Td>
                    <Td align="right" muted>{delivery.sent_at ? new Date(delivery.sent_at).toLocaleTimeString('en-IN') : '-'}</Td>
                    <Td align="right" muted>{delivery.opened_at ? new Date(delivery.opened_at).toLocaleTimeString('en-IN') : '-'}</Td>
                    <Td align="right">{delivery.attributed_revenue ? money(Number(delivery.attributed_revenue)) : '-'}</Td>
                  </tr>
                ))}
                {deliveries.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '28px', color: colors.textMuted }}>
                      No recipients match this filter yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="ntf-rail">
          <NotificationPreview
            platform="android"
            title={campaign.title}
            body={campaign.body}
            imageUrl={campaign.image_url}
            ctaLabel={campaign.cta_label}
          />

          <Card>
            <CardTitle>Why people were skipped</CardTitle>
            {(analytics?.suppression ?? []).length === 0 ? (
              <div style={{ fontSize: '13px', color: colors.textMuted }}>Nobody was skipped.</div>
            ) : (
              <div>
                {analytics?.suppression.map((row) => (
                  <div key={row.reason}>
                    <div className="ntf-row">
                      <span style={{ color: colors.textSecondary }}>
                        {SUPPRESSION_LABEL[row.reason] ?? row.reason}
                      </span>
                      <span className="ntf-row__value">{row.count.toLocaleString('en-IN')}</span>
                    </div>
                    {SUPPRESSION_RECOVERY[row.reason] ? (
                      <div style={{ fontSize: '11.5px', color: colors.textMuted, lineHeight: 1.45, paddingBottom: '8px' }}>
                        {SUPPRESSION_RECOVERY[row.reason]}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {(analytics?.failures ?? []).length > 0 ? (
            <Card>
              <CardTitle>Delivery failures</CardTitle>
              <div>
                {analytics?.failures.map((row) => (
                  <div key={row.error} className="ntf-row">
                    <span style={{ color: colors.textSecondary }}>
                      {PROVIDER_ERROR[row.error]?.label ?? row.error}
                    </span>
                    <span className="ntf-row__value">{row.count.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}

          <Card>
            <CardTitle>Audit</CardTitle>
            <div style={{ display: 'grid', gap: '6px', fontSize: '12px', color: colors.textMuted }}>
              <div>Created by {campaign.created_by_name || 'unknown'}</div>
              <div>Approved {campaign.approved_at ? new Date(campaign.approved_at).toLocaleString('en-IN') : 'Not yet'}</div>
              <div>Started {campaign.started_at ? new Date(campaign.started_at).toLocaleString('en-IN') : '-'}</div>
              <div>Completed {campaign.completed_at ? new Date(campaign.completed_at).toLocaleString('en-IN') : '-'}</div>
              <div>Opens: {campaign.deeplink || 'the app'}</div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
